import { useCallback, useEffect, useRef, useState } from "react";
import {
  paidCelebratableIds,
  paidCelebrationStorageKey,
  resolvePaidCelebration,
  type CelebratablePayment,
  type PaidCelebration,
} from "@/lib/paid-celebration";

/**
 * COL-103 — watches the tenant's payments for a flip to paid and hands the
 * dashboard one celebration at a time.
 *
 * The "already celebrated" set is persisted in localStorage, which is
 * deliberately per device: the point of the pop-up is the moment, and
 * re-showing it on every mount (the dashboard refetches and holds a realtime
 * subscription on `payments`) would be noise. A tenant who pays on their laptop
 * and then opens the dashboard on their phone will not see it a second time
 * there — and will not see it at all on the phone if the laptop got there
 * first. That is the accepted trade for not writing to the database.
 */

function parseSeen(raw: string | null): string[] | null {
  if (raw == null) return null;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map((id) => String(id)) : null;
  } catch {
    return null;
  }
}

function readSeen(key: string): string[] | null {
  try {
    return parseSeen(window.localStorage.getItem(key));
  } catch {
    // Private mode, disabled storage, or a corrupt value: fall back to
    // "nothing stored", which seeds instead of celebrating. Never throw here —
    // this runs inside the dashboard's render path.
    return null;
  }
}

function writeSeen(key: string, ids: string[]): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    // Storage full or blocked. The celebration still fires this session; it may
    // simply repeat on the next load, which is better than breaking the page.
  }
}

export function usePaidCelebration(input: {
  /** Null/undefined while payments are loading — no evaluation happens then. */
  payments: readonly CelebratablePayment[] | null | undefined;
  /** Null while auth resolves; storage is scoped per user. */
  userId: string | null | undefined;
}): {
  celebration: PaidCelebration | null;
  dismissCelebration: () => void;
} {
  const { payments, userId } = input;
  const [celebration, setCelebration] = useState<PaidCelebration | null>(null);
  // Mirrors what is in storage, so a re-render between a flip and the write
  // cannot fire the same celebration twice.
  const seenRef = useRef<{ key: string; ids: string[] } | null>(null);
  // Ids a sibling tab reported as seen that THIS tab's own `payments`/proofs
  // have not (yet) independently confirmed as paid. `resolvePaidCelebration`'s
  // `nextSeen` is always derived from local data alone, so without this a
  // lagging local poll would silently drop the sibling's id from storage
  // before this tab's own data catches up — erasing the sibling's write and
  // letting this tab re-celebrate the same payment once it does. Cleared as
  // soon as local data agrees, so it never overrides a genuine local un-mark.
  const pendingFromSiblingRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!userId || payments == null) return;

    const key = paidCelebrationStorageKey(userId);
    if (!seenRef.current || seenRef.current.key !== key) {
      // A new key (first mount, or a user switch without unmounting — e.g.
      // admin act-as-tenant) means any pin recorded so far belongs to a
      // different user's storage and must not leak into this one's.
      pendingFromSiblingRef.current = new Set();
      const stored = readSeen(key);
      seenRef.current = { key, ids: stored ?? [] };
      if (stored == null) {
        // First ever evaluation for this user on this device: record today's
        // paid rows silently so eight months of history do not all celebrate.
        const seeded = resolvePaidCelebration({
          payments,
          seen: [],
          seeded: false,
        });
        seenRef.current = { key, ids: seeded.nextSeen };
        writeSeen(key, seeded.nextSeen);
        return;
      }
    }

    const result = resolvePaidCelebration({
      payments,
      seen: seenRef.current.ids,
      seeded: true,
    });
    // Drop any pinned sibling id our own data now independently confirms —
    // once local truth agrees, `result.nextSeen` already covers it.
    const localPaidIds = new Set(paidCelebratableIds(payments));
    for (const id of pendingFromSiblingRef.current) {
      if (localPaidIds.has(id)) pendingFromSiblingRef.current.delete(id);
    }
    const nextSeen = Array.from(
      new Set([...result.nextSeen, ...pendingFromSiblingRef.current])
    );
    seenRef.current = { key, ids: nextSeen };
    writeSeen(key, nextSeen);
    if (result.celebrate) setCelebration(result.celebrate);
  }, [payments, userId]);

  // `seenRef` above only re-reads storage when its own key changes, i.e. once
  // per mount — a second open dashboard tab for the SAME user never sees the
  // first tab's write and can independently celebrate the same payment. The
  // `storage` event fires in sibling tabs (never the tab that wrote it), so
  // picking it up here keeps this tab's in-memory copy from going stale
  // without turning every resolve into a fresh localStorage read.
  useEffect(() => {
    if (!userId) return;
    const key = paidCelebrationStorageKey(userId);
    const onStorage = (event: StorageEvent) => {
      if (event.key !== key) return;
      // Read the event's own snapshot, not a fresh localStorage read: if this
      // tab's own lagging resolve writes in between the sibling's write and
      // this handler running, a live read would see OUR OWN overwrite instead
      // of the sibling's value and never learn about it at all.
      const stored = parseSeen(event.newValue);
      if (!stored) return;
      const storedSet = new Set(stored);
      const currentIds = new Set(seenRef.current?.ids ?? []);
      for (const id of storedSet) {
        if (!currentIds.has(id)) pendingFromSiblingRef.current.add(id);
      }
      // A sibling that stops reporting an id (a genuine un-mark, not just a
      // local lag) must release the pin too, or a later re-mark would be
      // suppressed forever by an id this tab never independently confirmed.
      for (const id of pendingFromSiblingRef.current) {
        if (!storedSet.has(id)) pendingFromSiblingRef.current.delete(id);
      }
      seenRef.current = { key, ids: stored };
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [userId]);

  const dismissCelebration = useCallback(() => setCelebration(null), []);

  return { celebration, dismissCelebration };
}
